import React, { memo, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Collapse,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import configServ from "../../../services/config";
import CheckAndReturn from "../../../utility/CheckAndReturn";
import LoaderNew from "../../Loader/LoaderNew";
import DownloadAllLedgersPdf from "../../Download/DownloadAllLedgersPdf";
import DownloadAllLedgersExcel from "../../Download/DownloadAllLedgersExcel";
import pdfImage from "../../Assets/images/pdf.jfif";
import excelImage from "../../Assets/images/excel.png";

const FILTER_FIELDS = [
  { id: "type", label: "TRANSACTION" },
  { id: "fname", label: "NAME" },
  { id: "entity_type", label: "TYPE" },
  { id: "entity_number", label: "REFERENCE NO." },
  { id: "is_credit", label: "CREDIT/DEBIT" },
  { id: "month", label: "MONTH" },
  { id: "amount", label: "AMOUNT" },
];

const TRANSACTION_OPTIONS = [
  { label: "Sales", value: "sale" },
  { label: "Purchase", value: "purchase" },
  { label: "Receipt", value: "receipt" },
  { label: "Payment", value: "payment" },
  { label: "Journal", value: "journal" },
  { label: "Contra", value: "contra" },
  { label: "Debit Note", value: "debit note" },
  { label: "Credit Note", value: "credit note" },
];

const ENTITY_TYPE_OPTIONS = [
  { label: "Customer", value: "customer" },
  { label: "Supplier", value: "supplier" },
];

const CREDIT_DEBIT_OPTIONS = [
  { label: "Credit", value: "true" },
  { label: "Debit", value: "false" },
];

const AMOUNT_OPTIONS = [
  { label: "Greater than 0", value: 0 },
  { label: "Greater than 1,000", value: 1000 },
  { label: "Greater than 10,000", value: 10000 },
  { label: "Greater than 1,00,000", value: 100000 },
  { label: "Greater than 10,00,000", value: 1000000 },
];

const TABLE_HEADERS = [
  { id: "type", label: "TRANSACTION" },
  { id: "fname", label: "NAME" },
  { id: "entity_type", label: "TYPE" },
  { id: "entity_number", label: "REFERENCE NO." },
  { id: "is_credit", label: "CREDIT/DEBIT" },
  { id: "month", label: "MONTH" },
  { id: "amount", label: "AMOUNT" },
];

const AllLedgers = () => {
  const {
    company_id,
    companyName,
    companyAddress,
    companyState,
    companyCountry,
  } = useSelector((state) => state.GlobalVariables);

  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [companyLogo, setCompanyLogo] = useState("");
  const [allLedgersData, setAllLedgersData] = useState([]);
  const rowsPerPageOptions = [20, 50, 100, 200];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("created_at");
  const [openRow, setOpenRow] = useState(null);

  const [filterField, setFilterField] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [monthFrom, setMonthFrom] = useState(null);
  const [monthTo, setMonthTo] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [nameOptions, setNameOptions] = useState([]);

  const getRowKey = (row, rowIndex) =>
    `${row.type}-${row.entity_number}-${row.created_at}-${rowIndex}`;

  const formatReturnable = (value) => {
    if (value === null || value === undefined) return "N/A";
    return value ? "Yes" : "No";
  };

  const headerCellStyle = {
    fontSize: 14,
    fontWeight: "bold",
    backgroundColor: "#4CB5F5",
    color: "white",
  };

  const cellStyle = { py: 0, my: 0 };

  const evenRowStyle = { background: "#f5f5f5" };
  const oddRowStyle = { background: "white" };
  const rowStyle = { "&:hover": { background: "#f1f9fe" } };

  const buildFilterPayload = (filters = appliedFilters) => ({
    company_id,
    page,
    per_page: rowsPerPage,
    sort_by: orderBy,
    order,
    filter_field: filters.filter_field || "",
    filter_value: filters.filter_value ?? "",
    month_from: filters.month_from || null,
    month_to: filters.month_to || null,
  });

  const assignCompanyLogo = async () => {
    try {
      const result = await configServ.company_logo({ company_id });
      if (result?.image) {
        setCompanyLogo(`data:image/png;base64,${result.image}`);
      } else {
        setCompanyLogo(null);
      }
    } catch (e) {
      setCompanyLogo(null);
    }
  };

  const fetchNameOptions = async () => {
    try {
      const [customerRes, supplierRes] = await Promise.all([
        configServ.getCustomerList({
          company_id,
          per_page: 5000,
          page: 0,
        }),
        configServ.getSuppliers({
          company_id,
          per_page: 5000,
          page: 0,
        }),
      ]);

      const customers = (customerRes?.data || []).map((c) => ({
        label: c.contact_name || c.fname || "",
        value: c.contact_name || c.fname || "",
        group: "Customer",
      }));

      const suppliers = (supplierRes?.data || []).map((s) => ({
        label: s.contact_name || s.fname || "",
        value: s.contact_name || s.fname || "",
        group: "Supplier",
      }));

      setNameOptions([...customers, ...suppliers].filter((x) => x.label));
    } catch (e) {
      console.log(e);
    }
  };

  const fetchAllLedgers = async (filters = appliedFilters) => {
    try {
      setLoading(true);
      const payload = {
        company_id,
        page,
        per_page: rowsPerPage,
        sort_by: orderBy,
        order,
        filter_field: filters.filter_field || "",
        filter_value:
          filters.filter_value !== undefined && filters.filter_value !== null
            ? filters.filter_value
            : "",
        month_from: filters.month_from || null,
        month_to: filters.month_to || null,
      };
      const res = await configServ.getAllLedgers(payload);
      if (res.status === 200) {
        setCount(res.total);
        setAllLedgersData(res.data);
      } else {
        setCount(0);
        setAllLedgersData([]);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchExportData = async () => {
    const res = await configServ.getAllLedgers({
      ...buildFilterPayload(),
      export_all: true,
    });
    if (res.status === 200) {
      return res.data || [];
    }
    return [];
  };

  useEffect(() => {
    assignCompanyLogo();
    fetchNameOptions();
  }, []);

  useEffect(() => {
    if (company_id) {
      fetchAllLedgers(appliedFilters);
    }
  }, [page, rowsPerPage, orderBy, order, company_id]);

  const handleFilterFieldChange = (event) => {
    const nextField = event.target.value;
    setFilterField(nextField);
    setFilterValue("");
    setMonthFrom(null);
    setMonthTo(null);
  };

  const handleApplyFilter = () => {
    if (!filterField) {
      setAppliedFilters({});
      setPage(0);
      fetchAllLedgers({});
      return;
    }

    const nextFilters = { filter_field: filterField };

    if (filterField === "month") {
      if (!monthFrom && !monthTo) {
        Swal.fire("Warning", "Please select a month range.", "warning");
        return;
      }
      nextFilters.month_from = monthFrom
        ? dayjs(monthFrom).format("YYYY-MM")
        : null;
      nextFilters.month_to = monthTo ? dayjs(monthTo).format("YYYY-MM") : null;
      nextFilters.filter_value = "";
    } else {
      if (filterValue === "" || filterValue === null || filterValue === undefined) {
        Swal.fire("Warning", "Please select or enter a filter value.", "warning");
        return;
      }
      nextFilters.filter_value = filterValue;
      nextFilters.month_from = null;
      nextFilters.month_to = null;
    }

    setAppliedFilters(nextFilters);
    setPage(0);
    fetchAllLedgers(nextFilters);
  };

  const handleClearFilter = () => {
    setFilterField("");
    setFilterValue("");
    setMonthFrom(null);
    setMonthTo(null);
    setAppliedFilters({});
    setPage(0);
    fetchAllLedgers({});
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadPdf = async () => {
    setOpenBackdrop(true);
    try {
      const rows = await fetchExportData();
      if (!rows.length) {
        Swal.fire("Info", "No data found to export.", "info");
        return;
      }
      DownloadAllLedgersPdf(rows, companyName, companyLogo, setOpenBackdrop);
    } catch (e) {
      setOpenBackdrop(false);
      Swal.fire("Error", "Failed to generate PDF.", "error");
    }
  };

  const handleDownloadExcel = async () => {
    setOpenBackdrop(true);
    try {
      const rows = await fetchExportData();
      if (!rows.length) {
        setOpenBackdrop(false);
        Swal.fire("Info", "No data found to export.", "info");
        return;
      }
      DownloadAllLedgersExcel(rows, companyName, setOpenBackdrop);
      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "Excel downloaded",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (e) {
      setOpenBackdrop(false);
      Swal.fire("Error", "Failed to generate Excel.", "error");
    }
  };

  const renderFilterValueInput = () => {
    if (!filterField) {
      return (
        <TextField
          size="small"
          fullWidth
          disabled
          label="Value"
          sx={{ backgroundColor: "white" }}
        />
      );
    }

    switch (filterField) {
      case "type":
        return (
          <Autocomplete
            size="small"
            options={TRANSACTION_OPTIONS}
            getOptionLabel={(option) => option.label || ""}
            value={
              TRANSACTION_OPTIONS.find((opt) => opt.value === filterValue) ||
              null
            }
            onChange={(event, newValue) =>
              setFilterValue(newValue?.value || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Transaction"
                sx={{ backgroundColor: "white" }}
              />
            )}
          />
        );

      case "fname":
        return (
          <Autocomplete
            size="small"
            options={nameOptions}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => option.label || ""}
            value={
              nameOptions.find((opt) => opt.value === filterValue) || null
            }
            onChange={(event, newValue) =>
              setFilterValue(newValue?.value || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Name"
                sx={{ backgroundColor: "white" }}
              />
            )}
          />
        );

      case "entity_type":
        return (
          <Autocomplete
            size="small"
            options={ENTITY_TYPE_OPTIONS}
            getOptionLabel={(option) => option.label || ""}
            value={
              ENTITY_TYPE_OPTIONS.find((opt) => opt.value === filterValue) ||
              null
            }
            onChange={(event, newValue) =>
              setFilterValue(newValue?.value || "")
            }
            renderInput={(params) => (
              <TextField {...params} label="Type" sx={{ backgroundColor: "white" }} />
            )}
          />
        );

      case "entity_number":
        return (
          <TextField
            size="small"
            fullWidth
            label="Reference No."
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            sx={{ backgroundColor: "white" }}
          />
        );

      case "is_credit":
        return (
          <Autocomplete
            size="small"
            options={CREDIT_DEBIT_OPTIONS}
            getOptionLabel={(option) => option.label || ""}
            value={
              CREDIT_DEBIT_OPTIONS.find((opt) => opt.value === filterValue) ||
              null
            }
            onChange={(event, newValue) =>
              setFilterValue(newValue?.value ?? "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Credit/Debit"
                sx={{ backgroundColor: "white" }}
              />
            )}
          />
        );

      case "month":
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <DatePicker
                label="From Month"
                views={["year", "month"]}
                value={monthFrom}
                onChange={(value) => setMonthFrom(value)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: { backgroundColor: "white" },
                  },
                }}
              />
              <DatePicker
                label="To Month"
                views={["year", "month"]}
                value={monthTo}
                onChange={(value) => setMonthTo(value)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: { backgroundColor: "white" },
                  },
                }}
              />
            </Stack>
          </LocalizationProvider>
        );

      case "amount":
        return (
          <Autocomplete
            size="small"
            options={AMOUNT_OPTIONS}
            getOptionLabel={(option) => option.label || ""}
            value={
              AMOUNT_OPTIONS.find(
                (opt) => String(opt.value) === String(filterValue)
              ) || null
            }
            onChange={(event, newValue) =>
              setFilterValue(newValue?.value ?? "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Amount Range"
                sx={{ backgroundColor: "white" }}
              />
            )}
          />
        );

      default:
        return null;
    }
  };

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, count - page * rowsPerPage);

  return (
    <>
      <Backdrop open={openBackdrop} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            All Ledger Report: {count}
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDownloadPdf}
              startIcon={
                <Box component="img" src={pdfImage} alt="PDF" sx={{ width: 18, height: 18 }} />
              }
            >
              PDF
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDownloadExcel}
              startIcon={
                <Box component="img" src={excelImage} alt="Excel" sx={{ width: 18, height: 18 }} />
              }
            >
              Excel
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: "#f8fafc", overflow: "visible" }}> */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" sx={{ backgroundColor: "white" }}>
              <InputLabel>Filter By</InputLabel>
              <Select
                label="Filter By"
                value={filterField}
                onChange={handleFilterFieldChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {FILTER_FIELDS.map((field) => (
                  <MenuItem key={field.id} value={field.id}>
                    {field.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            {renderFilterValueInput()}
          </Grid>
          <Grid item xs={12} md={4} sx={{ px: { xs: 0.5, md: 1 } }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ width: "100%", alignItems: "stretch" }}
            >
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleApplyFilter}
                sx={{ flex: 1, minWidth: 0, px: 1.5, whiteSpace: "nowrap" }}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ClearIcon />}
                onClick={handleClearFilter}
                sx={{ flex: 1, minWidth: 0, px: 1.5, whiteSpace: "nowrap" }}
              >
                Clear
              </Button>
            </Stack>
          </Grid>
        </Grid>
      {/* </Paper> */}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ flexGrow: 1, overflow: "auto", mt: 1 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyle} />
                <TableCell sx={headerCellStyle}>S.No.</TableCell>
                {TABLE_HEADERS.map((column) => (
                  <TableCell
                    key={column.id}
                    sortDirection={orderBy === column.id ? order : false}
                    sx={headerCellStyle}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading === false ? (
                allLedgersData.length > 0 ? (
                  allLedgersData.map((row, rowIndex) => {
                    const rowKey = getRowKey(row, rowIndex);
                    const hasItems =
                      Array.isArray(row.items) && row.items.length > 0;

                    return (
                      <React.Fragment key={rowKey}>
                        <TableRow
                          sx={{
                            ...rowStyle,
                            ...(rowIndex % 2 === 0 ? evenRowStyle : oddRowStyle),
                          }}
                        >
                          <TableCell sx={cellStyle}>
                            {hasItems && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setOpenRow(openRow === rowKey ? null : rowKey)
                                }
                              >
                                {openRow === rowKey ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell sx={cellStyle}>
                            {page * rowsPerPage + rowIndex + 1}
                          </TableCell>
                          <TableCell sx={cellStyle}>{row.type}</TableCell>
                          <TableCell sx={cellStyle}>{row.fname}</TableCell>
                          <TableCell sx={cellStyle}>{row.entity_type}</TableCell>
                          <TableCell sx={cellStyle}>{row.entity_number}</TableCell>
                          <TableCell sx={cellStyle}>
                            {Boolean(row.is_credit) ? "CREDIT" : "DEBIT"}
                          </TableCell>
                          <TableCell sx={cellStyle}>{row.month}</TableCell>
                          <TableCell sx={cellStyle}>
                            ₹{CheckAndReturn.roundToInteger(row.amount)}
                          </TableCell>
                        </TableRow>
                        {hasItems && (
                          <TableRow>
                            <TableCell sx={{ py: 0 }} colSpan={9}>
                              <Collapse in={openRow === rowKey} unmountOnExit>
                                <Box
                                  m={2}
                                  p={2}
                                  sx={{ bgcolor: "#fafafa", borderRadius: 1 }}
                                >
                                  <Table
                                    size="small"
                                    sx={{ borderRadius: 2, boxShadow: 3 }}
                                  >
                                    <TableHead
                                      sx={{ bgcolor: "#4CB5F5", color: "white" }}
                                    >
                                      <TableRow>
                                        <TableCell>Item</TableCell>
                                        <TableCell align="right">Qty</TableCell>
                                        <TableCell align="right">Rate</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell align="center">
                                          Returnable
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {row.items.map((item, itemIndex) => (
                                        <TableRow
                                          key={`${rowKey}-item-${itemIndex}`}
                                        >
                                          <TableCell>
                                            {item.itemname || "N/A"}
                                          </TableCell>
                                          <TableCell align="right">
                                            {item.qty ?? 0}
                                          </TableCell>
                                          <TableCell align="right">
                                            ₹{item.rate ?? 0}
                                          </TableCell>
                                          <TableCell align="right">
                                            ₹{item.amount ?? 0}
                                          </TableCell>
                                          <TableCell align="center">
                                            {formatReturnable(item.is_returnable)}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Box textAlign="center">
                        <Typography variant="body1" color="error">
                          No data found.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              ) : (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: "center" }}>
                    <LoaderNew />
                  </TableCell>
                </TableRow>
              )}

              {emptyRows > 0 && (
                <TableRow style={{ height: 1 * emptyRows }}>
                  <TableCell colSpan={9} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
};

export default memo(AllLedgers);
